import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Server, Cpu, HardDrive, Activity, Clock, StopCircle, RefreshCw } from 'lucide-react';
import { API_BASE, getAdminToken, colorMap, formatDate } from './adminUtils';

export const ServerTab = () => {
  const [serverStats, setServerStats] = useState(null);
  const [serverLoading, setServerLoading] = useState(false);
  const [activeVMs, setActiveVMs] = useState([]);
  const [stoppingVM, setStoppingVM] = useState(null);
  const [dbStats, setDbStats] = useState(null);
  const [dbStatsLoading, setDbStatsLoading] = useState(false);

  const fetchServerStats = useCallback(async () => {
    const token = getAdminToken(); if (!token) return;
    setServerLoading(true);
    try {
      const [s, v] = await Promise.all([
        fetch(`${API_BASE}/api/admin/server-stats`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_BASE}/api/admin/active-vms`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (s.ok) setServerStats(await s.json());
      if (v.ok) setActiveVMs((await v.json()).vms);
    } catch (e) { console.error(e); }
    finally { setServerLoading(false); }
  }, []);

  const fetchDbStats = useCallback(async () => {
    const token = getAdminToken(); if (!token) return;
    setDbStatsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/db-stats`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setDbStats(await res.json());
    } catch (e) { console.error(e); }
    finally { setDbStatsLoading(false); }
  }, []);

  useEffect(() => { fetchServerStats(); fetchDbStats(); }, []);

  const handleForceStopVM = async (vmName) => {
    const token = getAdminToken(); if (!token) return;
    setStoppingVM(vmName);
    try {
      const res = await fetch(`${API_BASE}/api/admin/force-stop-vm`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ vm_name: vmName })
      });
      if (res.ok) { toast.success((await res.json()).message); fetchServerStats(); }
    } catch { toast.error('Failed'); }
    finally { setStoppingVM(null); }
  };

  return (
    <div className="space-y-4">
      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardHeader><div className="flex items-center justify-between"><CardTitle className="text-white flex items-center gap-2"><Server className="w-5 h-5 text-blue-400" />Server Stats</CardTitle><Button variant="outline" size="icon" onClick={fetchServerStats} className="border-white/20 text-white"><RefreshCw className="w-4 h-4" /></Button></div></CardHeader>
        <CardContent>
          {serverLoading ? <div className="flex justify-center py-8"><RefreshCw className="w-8 h-8 text-amber-500 animate-spin" /></div> : serverStats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'CPU', value: `${serverStats.cpu.percent}%`, sub: `${serverStats.cpu.cores} cores`, icon: Cpu, color: 'blue' },
                { label: 'RAM', value: `${serverStats.memory.percent}%`, sub: `${serverStats.memory.used_gb}/${serverStats.memory.total_gb} GB`, icon: Activity, color: 'emerald' },
                { label: 'Disk', value: `${serverStats.disk.percent}%`, sub: `${serverStats.disk.used_gb}/${serverStats.disk.total_gb} GB`, icon: HardDrive, color: 'amber' },
                { label: 'Uptime', value: serverStats.uptime, sub: `${serverStats.process_count} procs`, icon: Clock, color: 'purple' },
              ].map((s, i) => (
                <Card key={i} className={colorMap[s.color].card}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3"><s.icon className={`w-8 h-8 ${colorMap[s.color].icon}`} /><div><p className={`text-xs ${colorMap[s.color].text}`}>{s.label}</p><p className="text-xl font-bold text-white">{s.value}</p><p className="text-xs text-slate-400">{s.sub}</p></div></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : <p className="text-slate-400 text-center">Unavailable</p>}
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardHeader><CardTitle className="text-white flex items-center gap-2"><Activity className="w-5 h-5 text-emerald-400" />Active VMs<Badge className="bg-emerald-500/20 text-emerald-300 ml-2">{activeVMs.length}</Badge></CardTitle></CardHeader>
        <CardContent>
          {activeVMs.length === 0 ? <p className="text-slate-400 text-center py-4">No active VMs</p> : (
            <div className="rounded-xl border border-white/10 overflow-hidden">
              <Table>
                <TableHeader><TableRow className="border-white/10"><TableHead className="text-slate-300">VM</TableHead><TableHead className="text-slate-300">User</TableHead><TableHead className="text-slate-300">Course</TableHead><TableHead className="text-slate-300">Started</TableHead><TableHead className="text-slate-300 text-center">Action</TableHead></TableRow></TableHeader>
                <TableBody>{activeVMs.map((v, i) => (<TableRow key={i} className="border-white/10"><TableCell className="text-white font-mono text-sm">{v.vm_name}</TableCell><TableCell className="text-slate-300">{v.user_email}</TableCell><TableCell className="text-slate-300">{v.course}</TableCell><TableCell className="text-slate-400 text-sm">{formatDate(v.created_at)}</TableCell><TableCell className="text-center"><Button variant="destructive" size="sm" onClick={() => handleForceStopVM(v.vm_name)} disabled={stoppingVM === v.vm_name} className="bg-red-500/20 hover:bg-red-500/30 text-red-300"><StopCircle className="w-4 h-4 mr-1" />{stoppingVM === v.vm_name ? '...' : 'Stop'}</Button></TableCell></TableRow>))}</TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Database Stats */}
      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2"><HardDrive className="w-5 h-5 text-purple-400" />Database Usage {dbStats?.warning && <Badge className="bg-red-500/20 text-red-300 animate-pulse ml-2">{dbStats.critical ? 'CRITICAL' : 'WARNING'}</Badge>}</CardTitle>
            <Button variant="outline" size="icon" onClick={fetchDbStats} className="border-white/20 text-white"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardHeader>
        <CardContent>
          {dbStatsLoading ? <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 text-purple-500 animate-spin" /></div> : dbStats ? (
            <div className="space-y-4">
              {/* Cluster Usage Bar */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-white">Cluster Usage (User Data Only)</span>
                  <span className={`text-sm font-bold ${dbStats.critical ? 'text-red-400' : dbStats.warning ? 'text-amber-400' : 'text-emerald-400'}`}>{dbStats.cluster_used_mb} MB / {dbStats.free_tier_limit_mb} MB ({dbStats.usage_percent}%)</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-4 overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${dbStats.critical ? 'bg-red-500' : dbStats.warning ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(dbStats.usage_percent, 100)}%` }} />
                </div>
                <p className="text-xs text-slate-500 mt-1">Free: {Math.max(0, (dbStats.free_tier_limit_mb - dbStats.cluster_used_mb)).toFixed(1)} MB remaining</p>
              </div>

              {/* Cluster Databases */}
              {dbStats.cluster?.databases?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-white mb-2">All Databases in Cluster ({dbStats.cluster.database_count})</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {dbStats.cluster.databases.filter(d => !d.empty).sort((a, b) => (a.system ? 1 : 0) - (b.system ? 1 : 0)).map((d, i) => (
                      <div key={i} className={`rounded-lg p-3 border ${d.system ? 'bg-white/3 border-white/5 opacity-50' : d.name === dbStats.database_name ? 'bg-purple-500/10 border-purple-500/30' : 'bg-white/5 border-white/10'}`}>
                        <p className={`text-sm font-mono truncate ${d.system ? 'text-slate-500' : d.name === dbStats.database_name ? 'text-purple-300' : 'text-white'}`}>{d.name}</p>
                        <p className={`text-lg font-bold ${d.system ? 'text-slate-500' : 'text-white'}`}>{d.size_mb} MB</p>
                        {d.name === dbStats.database_name && <p className="text-xs text-purple-400">Current DB</p>}
                        {d.system && <p className="text-xs text-slate-600">System (not counted)</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Current DB Summary */}
              <div>
                <h4 className="text-sm font-semibold text-purple-300 mb-2">{dbStats.database_name} — Detail</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white/5 rounded-lg p-3 text-center"><p className="text-xs text-purple-300">Collections</p><p className="text-xl font-bold text-white">{dbStats.total_collections}</p></div>
                  <div className="bg-white/5 rounded-lg p-3 text-center"><p className="text-xs text-blue-300">Documents</p><p className="text-xl font-bold text-white">{dbStats.total_documents.toLocaleString()}</p></div>
                  <div className="bg-white/5 rounded-lg p-3 text-center"><p className="text-xs text-emerald-300">Data + Index</p><p className="text-xl font-bold text-white">{dbStats.total_used_mb} MB</p></div>
                </div>
              </div>

              {/* Per Collection */}
              <div className="rounded-xl border border-white/10 overflow-hidden">
                <Table>
                  <TableHeader><TableRow className="border-white/10"><TableHead className="text-slate-300">Collection</TableHead><TableHead className="text-slate-300 text-right">Documents</TableHead><TableHead className="text-slate-300 text-right">Size (MB)</TableHead><TableHead className="text-slate-300 text-right">Indexes</TableHead></TableRow></TableHeader>
                  <TableBody>{dbStats.collections.map((c, i) => (
                    <TableRow key={i} className="border-white/10">
                      <TableCell className="text-white font-mono text-sm">{c.name}</TableCell>
                      <TableCell className="text-slate-300 text-right">{c.documents.toLocaleString()}</TableCell>
                      <TableCell className="text-slate-300 text-right">{c.size_mb}</TableCell>
                      <TableCell className="text-slate-400 text-right">{c.indexes}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              </div>
            </div>
          ) : <p className="text-slate-400 text-center py-4">Click refresh to load DB stats</p>}
        </CardContent>
      </Card>
    </div>
  );
};
